import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import * as React from "react"
import { toast } from "sonner"
import { type AdminSettingsUpdate, adminApi } from "@/api/admin"
import { ApiError } from "@/api/client"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

// Admin paneli "perde arkası" araç — operatör hangi sağlayıcının kullanıldığını
// görmek zorunda olduğu için burada gerçek isimleri gösteriyoruz. Dışarıda
// (dashboard / landing / public sayfalar) yalnızca "Hirefy AI" deniyor.
const PROVIDER_LABELS: Record<string, string> = {
  auto: "Otomatik (anahtara göre seç)",
  gemini: "Google Gemini",
  claude: "Anthropic Claude",
}

const ENV_LABELS: Record<string, string> = {
  anthropic_api_key: "Anthropic API anahtarı",
  gemini_api_key: "Gemini API anahtarı",
  resend_api_key: "Resend (e-posta)",
  stripe_secret_key: "Stripe",
  google_client_id: "Google OAuth",
  sentry_dsn: "Sentry",
}

export function AdminSettingsPage() {
  const qc = useQueryClient()
  const q = useQuery({ queryKey: ["admin-settings"], queryFn: () => adminApi.getSettings() })
  const [draft, setDraft] = React.useState<AdminSettingsUpdate | null>(null)

  React.useEffect(() => {
    if (q.data && draft === null) setDraft({ ...q.data.values })
  }, [q.data, draft])

  const save = useMutation({
    mutationFn: (body: AdminSettingsUpdate) => adminApi.updateSettings(body),
    onSuccess: () => {
      toast.success("Ayarlar kaydedildi.")
      qc.invalidateQueries({ queryKey: ["admin-settings"] })
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : "Kaydedilemedi."),
  })

  if (q.isLoading || !q.data || !draft) {
    return (
      <div className="space-y-3 p-6">
        <Skeleton className="h-6 w-1/3" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    )
  }

  const { options, env_status } = q.data
  const tempValue = typeof draft.ai_temperature === "number" ? draft.ai_temperature : 0.2

  return (
    <div className="space-y-6 p-6">
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">Sistem ayarları</h1>
        <p className="text-sm text-muted-foreground">
          Hirefy AI sağlayıcısı, modeller ve servis konfigürasyonu. Tüm
          organizasyonlar için geçerlidir.
        </p>
      </div>
      <Card>
          <CardContent className="space-y-5 p-6">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-medium">Hirefy AI</h2>
              <Badge variant="outline">Tüm organizasyonlar için geçerli</Badge>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Sağlayıcı stratejisi</Label>
                <Select
                  value={draft.ai_provider ?? "auto"}
                  onValueChange={(v) =>
                    setDraft({ ...draft, ai_provider: v as "auto" | "gemini" | "claude" })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {options.ai_provider.map((p) => (
                      <SelectItem key={p} value={p}>
                        {PROVIDER_LABELS[p] ?? p}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  "Otomatik" seçilirse ortam değişkenlerinde yapılandırılmış olan
                  sağlayıcı kullanılır.
                </p>
              </div>

              <div className="space-y-1.5">
                <Label>Sıcaklık (temperature)</Label>
                <div className="flex items-center gap-3">
                  <input
                    type="range"
                    min={0}
                    max={1.5}
                    step={0.05}
                    value={tempValue}
                    onChange={(e) =>
                      setDraft({ ...draft, ai_temperature: Number(e.target.value) })
                    }
                    className="w-full"
                  />
                  <span className="w-12 text-sm tabular-nums">{tempValue.toFixed(2)}</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Düşük değer daha tutarlı, yüksek değer daha yaratıcı yanıt verir.
                </p>
              </div>

              <div className="space-y-1.5">
                <Label>Gemini modeli</Label>
                <Select
                  value={draft.ai_model_gemini ?? options.ai_model_gemini[0]}
                  onValueChange={(v) => setDraft({ ...draft, ai_model_gemini: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {options.ai_model_gemini.map((m) => (
                      <SelectItem key={m} value={m}>
                        {m}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label>Claude modeli</Label>
                <Select
                  value={draft.ai_model_claude ?? options.ai_model_claude[0]}
                  onValueChange={(v) => setDraft({ ...draft, ai_model_claude: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {options.ai_model_claude.map((m) => (
                      <SelectItem key={m} value={m}>
                        {m}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="space-y-4 p-6">
            <h2 className="text-sm font-medium">Genel ayarlar</h2>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Gönderen e-posta (Resend)</Label>
                <Input
                  value={draft.resend_from ?? ""}
                  onChange={(e) =>
                    setDraft({ ...draft, resend_from: e.target.value || null })
                  }
                  placeholder="Hirefy <no-reply@hirefy.app>"
                />
                <p className="text-xs text-muted-foreground">
                  Boş bırakılırsa ortam değişkenindeki değer kullanılır.
                </p>
              </div>
              <div className="space-y-1.5">
                <Label>Uygulama tabanı (app_base_url)</Label>
                <Input
                  value={draft.app_base_url ?? ""}
                  onChange={(e) =>
                    setDraft({ ...draft, app_base_url: e.target.value || null })
                  }
                  placeholder="https://app.hirefy.com"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="space-y-3 p-6">
            <h2 className="text-sm font-medium">Ortam değişkeni durumu</h2>
            <p className="text-xs text-muted-foreground">
              Anahtarlar burada değiştirilmez — yalnızca yapılandırma durumu gösterilir.
              Düzenlemek için Coolify üzerinden ortam değişkenlerini güncelleyin.
            </p>
            <ul className="grid gap-2 sm:grid-cols-2">
              {Object.entries(env_status).map(([key, present]) => (
                <li
                  key={key}
                  className="flex items-center justify-between rounded-md border px-3 py-2 text-sm"
                >
                  <span>{ENV_LABELS[key] ?? key}</span>
                  {present ? (
                    <Badge variant="success">Yapılandırıldı</Badge>
                  ) : (
                    <Badge variant="outline">Eksik</Badge>
                  )}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        {save.error instanceof ApiError && (
          <Alert>
            <AlertDescription>{save.error.message}</AlertDescription>
          </Alert>
        )}

        <div className="flex justify-end">
          <Button
            disabled={save.isPending}
            onClick={() =>
              save.mutate({
                ai_provider: draft.ai_provider,
                ai_model_gemini: draft.ai_model_gemini,
                ai_model_claude: draft.ai_model_claude,
                ai_temperature: draft.ai_temperature,
                resend_from: draft.resend_from,
                app_base_url: draft.app_base_url,
              })
            }
          >
            Kaydet
          </Button>
        </div>
    </div>
  )
}
